import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormControlLabel, Switch, Typography } from '@mui/material';
import { uniq } from 'lodash';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { SelectDropdownWidget } from 'src/components/widget';
import { CodPositions, ScreenLayoutTypeEnum } from 'src/constants';
import { getGridOptions, getScreenName } from 'src/hooks/api/layouts/helpers';
import { useGlobalComponentsScreen } from 'src/hooks/useGlobalComponentsScreen';
import { IDisclaimerPosition, IDisclaimerWidth } from 'src/interfaces';
import {
  isCurrentScreenWithCOD,
  isCurrentScreenWithCODThankYou,
  isCurrentScreenWithDYR,
  isCurrentScreenWithDYS,
  lang,
  theme,
} from 'src/utils';
import { getSingleScreenConfigurationLabel } from 'src/utils/screen-config';

import { ScreenSetupProps } from './ScreenSetup.interface';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CodMessage,
  DisclaimerContainer,
  DisclaimerOpsContainer,
  Section,
} from './ScreenSetup.styled';

export const ScreenSetup = ({
  control,
  getValues,
  selectedChannelInfo,
  selectedLayoutType,
  errors,
  setValue,
  disclaimerFields,
  watch,
  isOutdoor,
  codPosition,
}: ScreenSetupProps) => {
  const selectedScreenLayoutType = watch('screen_layout_type') as
    | ScreenLayoutTypeEnum
    | undefined;
  const globalComponentsScreen = useGlobalComponentsScreen(
    undefined,
    selectedChannelInfo?.screens?.length,
    selectedScreenLayoutType,
  );

  const [expandedAccordion, setExpandedAccordion] = useState<number | null>(
    null,
  );

  const handleAccordionToggle = (index: number) => {
    setExpandedAccordion((prevIndex) => (prevIndex === index ? null : index));
  };

  const getErrorScreenIndices = () => {
    const indices = Object.keys(errors).map((key) =>
      parseInt(key.charAt(key.length - 1)),
    );

    return uniq(indices);
  };

  const onValidation = (index: number, name: 'grid' | 'placement' | 'size') => {
    if (disclaimerFields?.[index]) {
      if (getValues(`${name}_${index}`)) {
        return undefined;
      } else {
        return 'This field is required.';
      }
    } else {
      return undefined;
    }
  };

  return (
    <>
      <Typography variant="h6" marginBottom={2}>
        {lang.assemblyLayouts.screenSetup.title}
      </Typography>
      {selectedChannelInfo?.screens?.map((screen, index) => {
        const screenIndex =
          codPosition === CodPositions.RIGHT
            ? selectedChannelInfo.screens!.length - index // use a reversed index for right cod position
            : index + 1;
        const screenName =
          (selectedLayoutType &&
            getScreenName(
              selectedLayoutType,
              CodPositions.LEFT, // use default cod position to keep the order and match the design
              index + 1,
              selectedChannelInfo.screens?.length,
              selectedScreenLayoutType,
            )) ||
          '';
        const hasCustomerTriggeredEvents = [
          isCurrentScreenWithCOD,
          isCurrentScreenWithDYR,
          isCurrentScreenWithCODThankYou,
          isCurrentScreenWithDYS,
        ]
          .map((fn) => fn(screenIndex, globalComponentsScreen))
          .includes(true);

        return (
          <Accordion
            key={index}
            expanded={expandedAccordion === index}
            onChange={() => handleAccordionToggle(index)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                variant="body1"
                data-testid="ScreenHead"
                color={
                  getErrorScreenIndices().includes(index)
                    ? theme.palette.brand.error
                    : ''
                }>
                {screenName} screen -{' '}
                {getSingleScreenConfigurationLabel(screen)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Section>
                <Controller
                  name={`grid_${index}`}
                  control={control}
                  rules={{ required: 'This field is required.' }}
                  defaultValue=""
                  render={({ field, fieldState }) => (
                    <SelectDropdownWidget
                      {...field}
                      label={lang.assemblyLayouts.screenSetup.gridLabel}
                      value={field?.value}
                      onChange={(e) => {
                        field?.onChange(e);
                        setValue(`enableCustomerOrderDisplay`, false);
                        setValue(`enableDynamicArea`, false);
                        setValue(`enableCodTriggeredArea`, false);
                        setValue(`enablePromos`, false);
                        setValue(`enableThankYou`, false);
                      }}
                      selectOptions={getGridOptions(screen)}
                      hasError={!!fieldState?.error}
                      error={fieldState?.error?.message}
                    />
                  )}
                />
                {isOutdoor &&
                  hasCustomerTriggeredEvents &&
                  screenName !== 'Presell' && (
                    <CodMessage>
                      {lang.assemblyLayouts.screenSetup.codMessage}
                    </CodMessage>
                  )}
              </Section>
              <DisclaimerContainer>
                <Controller
                  name={`disclaimers.${index}`}
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field?.value}
                          onChange={(e) => {
                            field?.onChange(e.target.checked);
                            setValue(`placement_${index}`, '');
                            setValue(`size_${index}`, '');
                            setValue(`enableCustomerOrderDisplay`, false);
                            setValue(`enableDynamicArea`, false);
                            setValue(`enableCodTriggeredArea`, false);
                            setValue(`enablePromos`, false);
                            setValue(`enableThankYou`, false);
                          }}
                        />
                      }
                      label={lang.assemblyLayouts.screenSetup.disclaimerLabel}
                    />
                  )}
                />
                <DisclaimerOpsContainer>
                  <Controller
                    name={`placement_${index}`}
                    control={control}
                    rules={{
                      validate: () => onValidation(index, 'placement'),
                    }}
                    defaultValue=""
                    render={({ field, fieldState }) => (
                      <SelectDropdownWidget
                        {...field}
                        label={lang.assemblyLayouts.screenSetup.placementLabel}
                        disabled={!disclaimerFields?.[index]}
                        value={field?.value}
                        data-testid="Placement"
                        onChange={(e) => field?.onChange(e)}
                        selectOptions={[
                          {
                            label: IDisclaimerPosition.TOP,
                            value: IDisclaimerPosition.TOP,
                          },
                          {
                            label: IDisclaimerPosition.BOTTOM,
                            value: IDisclaimerPosition.BOTTOM,
                          },
                        ]}
                        hasError={!!fieldState?.error}
                        error={fieldState?.error?.message}
                      />
                    )}
                  />
                  <Controller
                    name={`size_${index}`}
                    control={control}
                    rules={{
                      validate: () => onValidation(index, 'size'),
                    }}
                    defaultValue=""
                    render={({ field, fieldState }) => (
                      <SelectDropdownWidget
                        {...field}
                        label={lang.assemblyLayouts.screenSetup.sizeLabel}
                        disabled={!disclaimerFields?.[index]}
                        value={field?.value}
                        onChange={(e) => field?.onChange(e)}
                        selectOptions={[
                          {
                            label: IDisclaimerWidth.HALF,
                            value: IDisclaimerWidth.HALF,
                          },
                          {
                            label: IDisclaimerWidth.FULL,
                            value: IDisclaimerWidth.FULL,
                          },
                        ]}
                        hasError={!!fieldState?.error}
                        error={fieldState?.error?.message}
                      />
                    )}
                  />
                </DisclaimerOpsContainer>
              </DisclaimerContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
};
